import router from '@/router';
import moment from 'moment';
import { showErrorNotification } from '@/utils/utils.js';
import { createRtmpInputs, startTestChannelRtmpInputs } from '@goldcast/api/backend';

export const RTMP_INPUT_DIALOG_STATES = {
  LOADING: 'LOADING',
  DELETING: 'DELETING',
  CREATING: 'CREATING',
  LOADED: 'LOADED',
  UPDATING: 'UPDATING',
  ERRORRED: 'ERRORRED',
  LIST_LOADING_ERROR: 'LIST_LOADING_ERROR'
};

export const RTMP_INPUT_STATES = {
  LOADING: 'LOADING',
  LOADED: 'LOADED',
  ERRORRED: 'ERRORRED',
  CANNOT_DELETE_IN_USE: 'CANNOT_DELETE_IN_USE'
};

function getFormattedRtmpInput(rtmpInput) {
  return {
    ...rtmpInput,
    state: RTMP_INPUT_STATES.LOADED,
    errorMessage: ''
  };
}

function isChannelInRunningState(channelState) {
  const stoppedStates = ['IDLE', 'NOT_PRESENT', 'STOPPED', 'DELETED'];
  return !stoppedStates.some(state => state === channelState);
}

function isChannelTestingTime(eventStartTimeStr, currentDateTime) {
  try {
    if (!eventStartTimeStr || !currentDateTime) return false;
    const autoRtmpStartTime = moment(eventStartTimeStr).subtract(1, 'hours');
    return autoRtmpStartTime.isAfter(currentDateTime);
  } catch (err) {
    return false;
  }
}

function getStreamStateMessage(channelState, isChannelRunning) {
  if (isChannelRunning) {
    if (channelState === 'STARTING') {
      return 'Preparing to accept stream';
    } else if (channelState === 'RECOVERING') {
      return 'Ready to receive incoming stream';
    } else {
      return 'Ready to receive incoming stream';
    }
  } else {
    return channelState === 'DELETED' ? 'Event has ended. Channel is archived.' : 'Not accepting incoming stream';
  }
}

function getRtmpTestBtnText(channelState, isChannelRunning, isTestingTime) {
  if (isChannelRunning) {
    if (channelState === 'STARTING') {
      return 'Connecting...';
    } else {
      return isTestingTime ? 'In Testing' : 'Running';
    }
  } else {
    return channelState === 'DELETED' ? 'Channel Archived' : 'Test RTMP In';
  }
}

function isTestBtnDisabled(channelState, isChannelRunning) {
  if (isChannelRunning) return true;

  return channelState === 'DELETED';
}

function getTestRunEndTime(testRunEndTimeStr) {
  if (!testRunEndTimeStr) return '';
  return moment(testRunEndTimeStr).format('hh:mm A');
}

function getDefaultStartTime(eventStartTimeStr) {
  if (!eventStartTimeStr) return '-';
  const autoRtmpStartTime = moment(eventStartTimeStr).subtract(1, 'hours');
  return autoRtmpStartTime.format('hh:mm A MMM Do, YYYY');
}

const state = {
  rtmpInputs: [],
  // Can be "LOADING", "DELETING", "CREATING", "LOADED", "UPDATING", "ERRORRED"
  rtmpInputsState: RTMP_INPUT_DIALOG_STATES.LOADED,
  createRtmpInputPopupVisible: false,
  progressMessage: 'Loading...',
  startTestChannelLoading: false,
  startTestChannelError: null
};

const getters = {
  getStartTestChannelLoading(state) {
    return state.startTestChannelLoading;
  },
  getStartTestChannelError(state) {
    return state.startTestChannelError;
  },
  allRtmpInputs(state, getters, rootState, rootGetters) {
    if (!state?.rtmpInputs) {
      return [];
    }
    const currentEvent = rootGetters['studioEventList/getCurrentEvent'];
    return state.rtmpInputs.map(rtmpInput => {
      const isTestingTime = isChannelTestingTime(currentEvent?.start_time, rootState?.clientClock?.currentDateTime);
      const isChannelRunning = isChannelInRunningState(rtmpInput?.channel_states);
      const isChannelTestRunning = isChannelRunning && rtmpInput?.channel_states !== 'STARTING' && isTestingTime;
      const inputStateMessage = getStreamStateMessage(rtmpInput?.channel_states, isChannelRunning, isTestingTime);
      const inputButtonText = getRtmpTestBtnText(rtmpInput?.channel_states, isChannelRunning, isTestingTime);
      const testBtnDisabled = isTestBtnDisabled(rtmpInput?.channel_states, isChannelRunning);
      const formattedTestEndTimeStr = getTestRunEndTime(rtmpInput?.test_run_end_time);
      const defaultStartTime = getDefaultStartTime(currentEvent?.start_time);
      const rtmpUrlSplit = (rtmpInput?.input_url || '').split('/');
      const streamKey = rtmpUrlSplit.pop();
      const streamUrl = rtmpUrlSplit.join('/');
      const failoverUrlSplit = (rtmpInput?.failover_input_url || '').split('/');
      const failoverStreamKey = failoverUrlSplit.pop();
      const failoverStreamUrl = failoverUrlSplit.join('/');

      return {
        ...rtmpInput,
        isChannelRunning,
        isTestingTime,
        isChannelTestRunning,
        inputStateMessage,
        inputButtonText,
        testBtnDisabled,
        formattedTestEndTimeStr,
        defaultStartTime,
        streamKey,
        streamUrl,
        failoverStreamKey,
        failoverStreamUrl
      };
    });
  },
  isTestEnabledForOneChannel(state, getters) {
    if (!(getters && getters['rtmpInputs/allRtmpInputs'])) return false;
    const rtmpInputs = getters['rtmpInputs/allRtmpInputs'] || [];
    return rtmpInputs.some(inputChannel => {
      return !!(inputChannel?.isChannelRunning && inputChannel?.isTestingTime);
    });
  }
};

const mutations = {
  setStartTestChannelLoading(state, isLoading) {
    state.startTestChannelLoading = isLoading;
  },
  setStartTestChannelError(state, error) {
    state.startTestChannelError = error;
  },
  setRtmpInputState(state, value) {
    state.rtmpInputsState = value || RTMP_INPUT_DIALOG_STATES.LOADED;
  },
  setRtmpLoadingMessage(state, message) {
    state.progressMessage = message || 'Loading...';
  },
  setRTMPInputs(state, value) {
    state.rtmpInputs = value.map(getFormattedRtmpInput);
  },
  showCreateInputPopup(state) {
    state.createRtmpInputPopupVisible = true;
  },
  hideCreateInputPopup(state) {
    state.createRtmpInputPopupVisible = false;
  },
  updateRtmpInput(state, updatedInputData) {
    if (!updatedInputData?.id) {
      return;
    }
    state.rtmpInputs = state.rtmpInputs.map(input => {
      if (input?.id === updatedInputData.id) {
        return {
          ...input,
          ...updatedInputData
        };
      } else {
        return input;
      }
    });
  },
  addInputToState(state, newInput) {
    if (!newInput?.id) return;
    state.rtmpInputs = [...state.rtmpInputs, newInput];
  },
  removeInputFromState(state, inputID) {
    state.rtmpInputs = [
      ...state.rtmpInputs.filter(input => {
        return input?.id !== inputID;
      })
    ];
  }
};

const actions = {
  processRtmpInputChannelUpdatedSocketMsg(context, message) {
    return new Promise(() => {
      if (message.data?.rtmp_input?.channel_states && message.data?.rtmp_input?.id) {
        context.commit('updateRtmpInput', {
          id: message.data?.rtmp_input?.id,
          channel_states: message.data?.rtmp_input?.channel_states,
          test_run_end_time: message.data?.rtmp_input?.test_run_end_time
        });
      }
    });
  },
  loadRtmpInputs(context, { onlyUpdateRealTimeChannelData = false, eventID }) {
    return new Promise((resolve, reject) => {
      const url = `/event/rtmp_inputs/?event=${eventID}`;
      context.commit('setRtmpInputState', RTMP_INPUT_DIALOG_STATES.LOADING);
      context.commit('setRtmpLoadingMessage', 'Loading Channels...');
      context
        .dispatch('makeapicall', { url, method: 'GET' }, { root: true })
        .then(inputs => {
          if (onlyUpdateRealTimeChannelData) {
            context.dispatch('updateRTMPRealTimeStates', inputs);
          } else {
            context.commit('setRTMPInputs', inputs);
          }

          context.commit('setRtmpInputState', RTMP_INPUT_DIALOG_STATES.LOADED);
          resolve();
        })
        .catch(() => {
          context.commit('setRtmpInputState', RTMP_INPUT_DIALOG_STATES.LIST_LOADING_ERROR);
          reject();
        });
    });
  },
  updateRTMPRealTimeStates(context, inputs = []) {
    inputs.forEach(input => {
      context.commit('updateRtmpInput', {
        id: input.id,
        state: input.state
      });
    });
  },
  createRtmpInput(context, { inputName, eventId }) {
    return new Promise((resolve, reject) => {
      context.commit('setRtmpInputState', RTMP_INPUT_DIALOG_STATES.LOADING);
      context.commit('setRtmpLoadingMessage', 'Creating the Channel...');
      createRtmpInputs({
        body: {
          name: inputName,
          event: eventId
        }
      })
        .then(newInput => {
          const formattedInput = getFormattedRtmpInput(newInput);
          context.commit('setRtmpInputState', RTMP_INPUT_DIALOG_STATES.LOADED);
          context.commit('addInputToState', formattedInput);
          context.commit('hideCreateInputPopup');
          resolve(formattedInput);
        })
        .catch(() => {
          context.commit('setRtmpInputState', RTMP_INPUT_DIALOG_STATES.ERRORRED);
          reject();
        });
    });
  },
  startRtmpInChannelForTesting(context, inputID) {
    return new Promise((resolve, reject) => {
      if (!inputID) return;
      context.commit('setStartTestChannelLoading', true);

      context.commit('setStartTestChannelError', false);
      context.commit('setRtmpInputState', RTMP_INPUT_DIALOG_STATES.LOADING);
      context.commit('setRtmpLoadingMessage', 'Starting RTMP channel for testing...');
      return startTestChannelRtmpInputs({ id: inputID, body: {} })
        .then(() => {
          context.commit('setRtmpInputState', RTMP_INPUT_DIALOG_STATES.LOADED);
          context.commit('updateRtmpInput', {
            id: inputID,
            channel_states: 'STARTING'
          });
          context.commit('setStartTestChannelLoading', false);
          return resolve();
        })
        .catch(() => {
          context.commit('setRtmpInputState', RTMP_INPUT_DIALOG_STATES.ERRORRED);
          showErrorNotification(
            'Testing did not start due to an error! Contact support@goldcast.io if the problem persists.',
            6000
          );
          context.commit('setStartTestChannelError', true);
          reject();
        })
        .finally(() => {
          context.commit('setStartTestChannelLoading', false);
          context.commit('setRtmpLoadingMessage', '');
        });
    });
  }
};

export default {
  state,
  getters,
  mutations,
  actions,
  namespaced: true
};
